import { isEmpty, isString } from 'lodash'
import * as Yup from 'yup'

// The bare-path grammar, for connectors whose payload has a single source and
// therefore needs no "<prefix>:" qualifier. TCN's own token grammar stays in
// tcn/tokenSources.js; this is the degenerate case of it.
//
// Paths are glom expressions resolved server-side by parse_json_path. Validation
// here is deliberately shallow -- shape only, never semantics. The backend owns what
// a path means, and a second implementation of that would drift.

export const PATH_PLACEHOLDER = 'call.id'

export const PATH_MESSAGE =
  'Enter a path like call.id, data.agent.email or rows[fieldLabel=DBCLNT].fieldValue'

// The server understands exactly two bracketed forms, and nothing else:
//
//   key[field=value]   select the list element whose field equals value
//   key[field?]        select the list element that HAS that key
//
// Mirrors _PREDICATE_RE and _PRESENCE_RE in src/utils/glom_parser.py. This is still
// shape, not semantics -- what a path *means* stays the server's business -- but a
// bracketed segment in any other shape is malformed whatever the payload looks like,
// so the form can say so rather than letting it save and resolve to nothing.
//
// The "?" is required and is not decoration. "skills[name]" is the mistake this
// catches: someone meant "skills[name=Ubiquity]" and dropped the value. The server
// refuses it deliberately, because every element of that list has a "name" key, so
// treating it as presence would silently return the first element.
//
// Note there is no index form. "events[0]" resolves to nothing server-side; glom
// addresses position as a plain segment, "events.0.type".
const PREDICATE_SEGMENT = /^[^[\]]*\[[^=\]]+=[^\]]*\]$/
const PRESENCE_SEGMENT = /^[^[\]]*\[[^=\]]+\?\]$/

const isValidSegment = (segment) => {
  if (isEmpty(segment)) {
    return false
  }
  // A segment with no brackets at all is a plain key; the "|json" suffix lives here
  // too and needs nothing special.
  if (!segment.includes('[') && !segment.includes(']')) {
    return true
  }
  return PREDICATE_SEGMENT.test(segment) || PRESENCE_SEGMENT.test(segment)
}

/**
 * Whether a string is shaped like a path the server could resolve.
 *
 * Splits on "." treating "[...]" as opaque -- exactly as the server's
 * _split_segments does, so a predicate value containing a dot or a colon stays
 * inside its value -- then checks each segment against the grammar above.
 */
export const isValidPath = (path) => {
  if (!isString(path) || isEmpty(path.trim())) {
    return false
  }

  const trimmed = path.trim()
  let depth = 0
  let segment = ''

  for (const char of trimmed) {
    if (char === '[') {
      depth += 1
    } else if (char === ']') {
      depth = Math.max(depth - 1, 0)
    }

    if (char === '.' && depth === 0) {
      if (!isValidSegment(segment)) {
        return false
      }
      segment = ''
    } else {
      segment += char
    }
  }

  // depth > 0 is an unclosed bracket. A stray "]" with no "[" leaves depth at 0 but
  // fails the segment grammar, which is why both checks are needed.
  return isValidSegment(segment) && depth === 0
}

// nullable() for the same reason as optionalPathSchema below: an unset column comes
// back from the backend as null, and buildDefaults only substitutes a *truthy*
// defaultValue, so a null reaches Yup as-is. Without it a freshly created config
// renders Yup's own "must be a `string` type ... mark the schema as `.nullable()`"
// text in the form instead of this field's own message.
//
// The shape test skips the empty case so required() owns it and only one message
// shows; without that, a null fails both and the shape message can win.
export const requiredPathSchema = (label) =>
  Yup.string()
    .nullable()
    .required(`${label} is required`)
    .test('valid-path', PATH_MESSAGE, (path) => isEmpty(path) || isValidPath(path))

// An empty optional path means "not mapped", which is a legitimate choice, so only a
// non-empty value is shape-checked.
//
// nullable() is required, not tidiness: an unset column comes back from the backend
// as null, and buildDefaults only substitutes a *truthy* defaultValue -- so a null
// reaches Yup as-is. Without this, every freshly created config would fail validation
// on mount and disable Save with nothing on screen explaining why.
export const optionalPathSchema = Yup.string()
  .nullable()
  .test('valid-path', PATH_MESSAGE, (path) => isEmpty(path) || isValidPath(path))

/**
 * Codec for a row that is nothing but a path.
 *
 * The shared row components speak TCN's { prefix, path } shape; a single-source
 * connector supplies this so the same components work with no prefix control and no
 * forked rendering.
 */
export const barePathCodec = {
  split: (token) => ({ prefix: '', path: isString(token) ? token : '' }),
  join: (prefix, path) => path || '',
  isQualified: isValidPath,
}

/** Codec for a row holding a literal value rather than a path (event type values). */
export const plainValueCodec = {
  split: (value) => ({ prefix: '', path: isString(value) ? value : '' }),
  join: (prefix, value) => value || '',
  isQualified: (value) => isString(value) && !isEmpty(value.trim()),
}
